import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { CategoryForm } from "./components/CategoryForm";
import { CategoryList } from "./components/CategoryList";
import { useCategories } from "./hooks/useCategories";
import type { Category } from "@/types/models";

export default function CategoriesPage() {
  const {
    store,
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategory,
    saving,
    deleting,
  } = useCategories();

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = (input: Parameters<typeof createCategory>[0]) => {
    setMessage(null);

    if (input.id) {
      updateCategory(input, {
        onCompleted: () => {
          setMessage("Categoria atualizada com sucesso.");
          setSelectedCategory(null);
        },
      });
      return;
    }

    createCategory(input, {
      onCompleted: () => setMessage("Categoria criada com sucesso."),
    });
  };

  const handleDelete = (category: Category) => {
    if (!window.confirm(`Excluir a categoria "${category.name}"?`)) return;

    setMessage(null);
    deleteCategory(category.id, {
      onCompleted: () => {
        setMessage("Categoria excluída.");
        if (selectedCategory?.id === category.id) setSelectedCategory(null);
      },
    });
  };

  if (loading) {
    return (
      <div className="container-elegant page-padding space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="container-elegant page-padding">
        <PageHeader title="Categorias" />
        <section className="max-w-xl border border-primary-200 dark:border-primary-800 panel-padding">
          <h2 className="text-xl font-light text-primary-900 dark:text-white mb-3">
            Crie sua loja primeiro
          </h2>
          <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
            Você precisa configurar uma loja antes de organizar categorias de produtos.
          </p>
          <Button asChild>
            <Link to="/store/settings">CONFIGURAR LOJA</Link>
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="container-elegant page-padding">
      <PageHeader
        title="Categorias"
        description={`Organize os produtos da loja ${store.name} em categorias.`}
      />

      {message && (
        <p className="mb-6 text-sm font-light text-green-700 dark:text-green-400">{message}</p>
      )}

      <div className="grid gap-10 lg:grid-cols-2">
        <CategoryList
          categories={categories}
          selectedId={selectedCategory?.id}
          deleting={deleting}
          onEdit={setSelectedCategory}
          onDelete={handleDelete}
        />
        <CategoryForm
          selectedCategory={selectedCategory}
          saving={saving}
          onSubmit={handleSubmit}
          onCancelEdit={() => setSelectedCategory(null)}
        />
      </div>
    </div>
  );
}
